import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import * as types from '../mutation-types';
import AgentAPI from '../../api/agents';

export const state = {
  records: [],
  conversations: {},
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
  },
};

export const getters = {
  getAgents($state) {
    return $state.records;
  },
  getVerifiedAgents($state) {
    return $state.records.filter(record => record.confirmed);
  },
  getUIFlags($state) {
    return $state.uiFlags;
  },
  getAgentChats: $state => agentId => {
    return $state.conversations[agentId] || [];
  },
  getAgentCountByStatus: $state => ids => {
    const considered = ids
      ? $state.records.filter(({ id }) => ids.includes(id))
      : $state.records;

    let status = {
      online: considered.filter(agent => agent.availability_status === 'online')
        .length,
      busy: considered.filter(agent => agent.availability_status === 'busy')
        .length,
      offline: considered.filter(
        agent => agent.availability_status === 'offline'
      ).length,
    };
    return status;
  },
};

export const actions = {
  get: async ({ commit }) => {
    commit(types.default.SET_AGENT_FETCHING_STATUS, true);
    try {
      const response = await AgentAPI.get();
      commit(types.default.SET_AGENT_FETCHING_STATUS, false);
      commit(types.default.SET_AGENTS, response.data);
    } catch (error) {
      commit(types.default.SET_AGENT_FETCHING_STATUS, false);
    }
  },
  create: async ({ commit }, agentInfo) => {
    commit(types.default.SET_AGENT_CREATING_STATUS, true);
    try {
      const response = await AgentAPI.create(agentInfo);
      // Strip generated_password before committing — keeping it in the Vuex
      // record would persist the secret for the rest of the session and leak
      // through devtools / Sentry breadcrumbs. password_status is per-request
      // too, so it must not stick to the agent record either.
      const { generated_password, password_status, ...agentRecord } =
        response.data;
      commit(types.default.ADD_AGENT, agentRecord);
      commit(types.default.SET_AGENT_CREATING_STATUS, false);
      return { ...agentRecord, generated_password, password_status };
    } catch (error) {
      commit(types.default.SET_AGENT_CREATING_STATUS, false);
      throw error;
    }
  },
  update: async ({ commit }, { id, ...agentParams }) => {
    commit(types.default.SET_AGENT_UPDATING_STATUS, true);
    try {
      const response = await AgentAPI.update(id, agentParams);
      // Strip generated_password before committing — keeping it in the Vuex
      // record would persist the secret for the rest of the session and leak
      // through devtools / Sentry breadcrumbs.
      const { generated_password, ...agentRecord } = response.data;
      commit(types.default.EDIT_AGENT, agentRecord);
      commit(types.default.SET_AGENT_UPDATING_STATUS, false);
      return { ...agentRecord, generated_password };
    } catch (error) {
      commit(types.default.SET_AGENT_UPDATING_STATUS, false);
      throw new Error(error);
    }
  },
  updateSingleAgentPresence: ({ commit }, { id, availabilityStatus }) => {
    commit(types.default.UPDATE_SINGLE_AGENT_PRESENCE, {
      id,
      availabilityStatus,
    });
  },
  updatePresence: async ({ commit }, data) => {
    commit(types.default.UPDATE_AGENTS_PRESENCE, data);
  },
  delete: async ({ commit }, agentId) => {
    commit(types.default.SET_AGENT_DELETING_STATUS, true);
    try {
      await AgentAPI.delete(agentId);
      commit(types.default.DELETE_AGENT, agentId);
      commit(types.default.SET_AGENT_DELETING_STATUS, false);
    } catch (error) {
      commit(types.default.SET_AGENT_DELETING_STATUS, false);
      throw new Error(error);
    }
  },
  listAllConversations: async ({ commit }, { agentId, filter }) => {
    const response = await AgentAPI.listConversations(agentId, { filter });
    commit(types.default.SET_AGENT_CONVERSATIONS, {
      agentId,
      conversations: response.data.payload,
    });
  },
};

export const mutations = {
  [types.default.SET_AGENT_FETCHING_STATUS]($state, status) {
    $state.uiFlags.isFetching = status;
  },
  [types.default.SET_AGENT_CREATING_STATUS]($state, status) {
    $state.uiFlags.isCreating = status;
  },
  [types.default.SET_AGENT_UPDATING_STATUS]($state, status) {
    $state.uiFlags.isUpdating = status;
  },
  [types.default.SET_AGENT_DELETING_STATUS]($state, status) {
    $state.uiFlags.isDeleting = status;
  },
  [types.default.SET_AGENT_CONVERSATIONS]($state, { agentId, conversations }) {
    // Use Vue.set to ensure reactivity
    $state.conversations = {
      ...$state.conversations,
      [agentId]: conversations,
    };
  },

  [types.default.SET_AGENTS]: MutationHelpers.set,
  [types.default.ADD_AGENT]: MutationHelpers.create,
  [types.default.EDIT_AGENT]: MutationHelpers.update,
  [types.default.DELETE_AGENT]: MutationHelpers.destroy,
  [types.default.UPDATE_AGENTS_PRESENCE]: MutationHelpers.updatePresence,
  [types.default.UPDATE_SINGLE_AGENT_PRESENCE]: (
    $state,
    { id, availabilityStatus }
  ) =>
    MutationHelpers.updateSingleRecordPresence($state.records, {
      id,
      availabilityStatus,
    }),
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
